
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AIMessage, VerbosityLevel } from './types';
import { openAiService } from '@/services/ai';

export const useChatMessages = () => {
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [verbosityLevel, setVerbosityLevel] = useState<VerbosityLevel>('balanced');
  const [isProcessing, setIsProcessing] = useState(false);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          role: 'system',
          content: "Welcome to DirectPress AI Assistant! I can help you understand news articles, explore topics in depth, or find specific information. Ask me about any news topic or article you're reading.",
          timestamp: new Date(),
        }
      ]);
    }
  }, [messages.length]);

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    // Generate a unique ID for the message
    const messageId = `msg-${Date.now()}`;
    
    // Add user message
    setMessages(prev => [
      ...prev,
      {
        id: messageId,
        role: 'user',
        content: message,
        timestamp: new Date(),
      }
    ]);
    
    // Add loading message from assistant
    const assistantMessageId = `msg-${Date.now() + 1}`;
    setMessages(prev => [
      ...prev,
      {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      }
    ]);
    
    setIsProcessing(true);
    
    try {
      // Call the OpenAI API through our edge function
      console.log('Sending message to AI:', message);
      const response = await openAiService.chatWithAI(message, verbosityLevel);
      console.log('AI response received:', response);
      
      if (response.success && response.data) {
        // Update the assistant message with the real response
        setMessages(prev => 
          prev.map(msg => 
            msg.id === assistantMessageId 
              ? {
                  ...msg,
                  content: response.data.response || "I'm sorry, I couldn't process that request.",
                  isLoading: false,
                  sources: response.data.sources || [
                    {
                      title: 'The Future of Renewable Energy in Urban Settings',
                      url: '/article/1',
                    },
                    {
                      title: 'Climate Policy in the 21st Century',
                      url: '/article/2',
                    }
                  ]
                } 
              : msg
          )
        );
      } else {
        throw new Error(response.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error sending message to AI:', error);
      
      // Update the message to show the error
      setMessages(prev => 
        prev.map(msg => 
          msg.id === assistantMessageId 
            ? {
                ...msg,
                content: "I'm sorry, I encountered an error processing your request. Please try again later.",
                isLoading: false
              } 
            : msg
        )
      );
      
      toast.error('Failed to get response from AI assistant');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const clearMessages = () => {
    setMessages([
      {
        id: 'welcome',
        role: 'system',
        content: "Welcome to DirectPress AI Assistant! I can help you understand news articles, explore topics in depth, or find specific information. Ask me about any news topic or article you're reading.",
        timestamp: new Date(),
      }
    ]);
    toast.success('Conversation cleared');
  };
  
  const rateResponse = (messageId: string, rating: 'helpful' | 'not-helpful') => {
    // In a real app, you would send this feedback to your backend
    toast.success(`Thank you for your feedback! You rated this response as ${rating}.`);
  };

  return {
    messages,
    sendMessage,
    clearMessages,
    rateResponse,
    verbosityLevel,
    setVerbosityLevel,
    isProcessing
  };
};
